import { FC, useState, useEffect } from "react";
import ModalComponent, {
  ModalProps,
} from "../../../../../common/components/modal/modal.component";
import ButtonComponent from "../../../../../common/components/button/button.component";
import orderTranslationsHelper from "../../../../../languages/order-translations.helper";
import FormFieldComponent from "../../../../../common/components/form/field/form-field.component";
import OrderDetailsSelectTaxiContractsComponent from "../common/select/order-details-select-taxi-contracts.component";
import notificationService from "../../../../../common/utils/notification/notification.service";
import useOrderDetailsForwardingForm from "../hooks/use-order-details-forwarding-form";
import SelectOption from "../../../../../common/components/form/select/common/option/select-option";
import useOrderDetailsForwarding from "../use-order-details-forwarding";
import OrderDetailsTaxiCorporationSelectComponent from "./types/order-details-taxi-corporation-select-component";
import useAbort from "../../../../../common/hooks/use-abort";
import orderForwardService from "../../../../../common/services/order-forward/order-forward.service";
import OrderForwardEditParams from "../../../../../common/services/order-forward/edit/order-forward-edit-params";

type OrderDetailsEditForwardingModalComponentProps = Pick<
  ModalProps,
  "isOpen" | "onClose"
> & {
  transportingOrderId: string | undefined;
  submitDisabledText: string;
};

const OrderDetailsEditForwardingModalComponent: FC<
  OrderDetailsEditForwardingModalComponentProps
> = ({ isOpen, transportingOrderId, submitDisabledText, onClose }) => {
  const [contractId, setContractId] = useState("");
  const [selectedTaxiContract, setSelectedTaxiContract] =
    useState<OrderDetailsTaxiCorporationSelectComponent | null>(null);

  useEffect(() => {
    getForwardings();
  }, []);

  const { formValidationResults, setTaxiContract, checkIsFormValid } =
    useOrderDetailsForwardingForm({ onlyContract: true });

  const translations =
    orderTranslationsHelper.getDetailsTranslations().editForwarding;

  const transportingOrderEditForwardingSignal = useAbort();

  const {
    form,
    headingText,
    successEditForwardingNotificationText,
    failureEditForwardingNotificationText,
  } = translations;
  const {
    submitButtonTitle,
    submitButtonText,
    rejectButtonTitle,
    rejectButtonText,
    taxiContractLabel,
  } = form;

  const { producerTaxiId, forwardingId, consumerTaxiId, getForwardings } =
    useOrderDetailsForwarding({
      transportingOrderId,
    });

  const isEditEnable = !consumerTaxiId || !forwardingId;

  const submitText = !consumerTaxiId ? submitDisabledText : submitButtonTitle;

  const handleOnChange = (value: SelectOption) => {
    setSelectedTaxiContract(value);
    setContractId(value.value.id);
    getForwardings();
    setTaxiContract(value);
  };

  const handleEditForwardingSubmitFailure = () => {
    notificationService.error(failureEditForwardingNotificationText);
    onClose();
  };

  const handleEditForwardingSubmitSuccess = () => {
    notificationService.success(successEditForwardingNotificationText);
    onClose();
  };

  const handleOnSubmit = () => {
    const isFormValid = checkIsFormValid();
    if (!isFormValid) return;

    const params: OrderForwardEditParams = {
      contractId: contractId,
      forwardingId: forwardingId,
    };

    orderForwardService
      .editForwarding(params, transportingOrderEditForwardingSignal.signal)
      .then(handleEditForwardingSubmitSuccess)
      .catch(handleEditForwardingSubmitFailure);
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      classNames={{
        content: `modal_content`,
      }}
      header={{
        title: headingText,
      }}
      actions={[
        <ButtonComponent
          onClick={handleOnSubmit}
          type="primary"
          title={submitText}
          isDisabled={isEditEnable}
        >
          {submitButtonText}
        </ButtonComponent>,
        <ButtonComponent
          type="brand"
          onClick={onClose}
          title={rejectButtonTitle}
        >
          {rejectButtonText}
        </ButtonComponent>,
      ]}
    >
      <FormFieldComponent
        label={taxiContractLabel}
        classNames={{ root: "mt-0" }}
        isRequired
        errorMessage={formValidationResults.taxiContract.errorMessage}
      >
        <OrderDetailsSelectTaxiContractsComponent
          onChange={handleOnChange}
          taxiCorporationUuid={consumerTaxiId}
          selectedTaxiContract={selectedTaxiContract}
          isEditEnable={isEditEnable}
          producerTaxiId={producerTaxiId}
        />
      </FormFieldComponent>
    </ModalComponent>
  );
};

export default OrderDetailsEditForwardingModalComponent;
